import {Component, EventEmitter, HostListener, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {NgClass} from '@angular/common';
import {ConnectedPosition} from '@angular/cdk/overlay';
import {SpinnerComponent} from '../../../../../lib/components/spinner/spinner.component';
import * as Papa from 'papaparse';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import {DeviceId, DeviceType, DEVICE_TYPE_DISPLAY} from '../../../../../shared/models/device';
import {BacnetPoint} from '../../../../../shared/models/point';
import {ModbusPoint} from '../../../../../shared/models/modbus';
import {LoRaWANPoint} from '../../../../../shared/models/lorawan';
import {TileProcessingItem, TileProcessingStatus} from '../../../shared/models/csv-tile';
import { CSVTemplateGenerator } from '../../utils/csv-template-generator.util';
import { CSVFileValidator } from '../../../shared/utils/csv-file-validator.util';
import { BULK_CREATION_PROTOCOL_HANDLERS, BulkCreationProtocolHandler } from '../../utils/bulk-creation-protocol-handlers';
import {FaIconComponent} from '@src/lib/components/fa-icon/fa-icon.component';
import { CsvTileImporterService } from '../../../shared/services/csv-tile-importer.service';
import { ShadeToolsService } from '../../../../../core/services/shade-tools.service';
import { FeatureFlagsService, FeatureFlag } from '../../../../../core/services/feature-flags.service';
import { MotiOption, MotiOptionGroup } from '../../../../../moti/models/moti-option';
import { PopoverMenuComponent } from '../../../../../moti/components/popover-menu/popover-menu.component';
import { DialogBoxButtonColor } from '../../../../../shared/models/dialog-box';
import { FORMULA_TEMPLATES } from '@features/formula-tile/shared/constants/formula-templates.constants';
import { CSVConfigExporter } from '../../../shared/utils/csv-config-exporter.util';
import { FormulaService } from '@features/formula-tile/shared/services/formula.service';
import { FormulaType } from '../../../../../shared/models/formula-config';

@Component({
  selector: 'motn-bulk-tile-creation-upload',
  templateUrl: './bulk-tile-creation-upload.component.html',
  styleUrls: ['./bulk-tile-creation-upload.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgClass,
    SpinnerComponent,
    FaIconComponent,
    PopoverMenuComponent,
  ],
})
export class BulkTileCreationUploadComponent implements OnInit {
  @Input() deviceId: DeviceId;
  @Input() points: BacnetPoint[] | ModbusPoint[] | LoRaWANPoint[];
  @Input() protocol: DeviceType;
  @Output() close = new EventEmitter<void>();
  @Output() proceedToProcessing = new EventEmitter<TileProcessingItem[]>();

  selectedFile: File | null = null;
  processingUpload: boolean = false;
  validatingFormulas: boolean = false;
  selectedProtocol: DeviceType | null = null;

  // Protocol selection menu state
  protocolMenuGroup: MotiOptionGroup<DeviceType> = new MotiOptionGroup<DeviceType>([]);
  toggleProtocolMenuGroup: MotiOptionGroup<DeviceType> = new MotiOptionGroup<DeviceType>([]);
  numericMenuOpen: boolean = false;
  toggleMenuOpen: boolean = false;

  // Formula template sub-menu (shown when Formula Tile is selected from numeric dropdown)
  formulaNumericMenuOpen: boolean = false;
  readonly formulaTemplateGroup = new MotiOptionGroup<string | null>([
    ...FORMULA_TEMPLATES.map(t => ({ id: t.id, name: t.name })),
    { id: null, name: 'Custom Formula' },
  ]);

  // Popover positioning
  overlayPositions: ConnectedPosition[] = [{
    originX: 'start',
    originY: 'bottom',
    overlayX: 'start',
    overlayY: 'top',
  }];

  // Expose DeviceType to template
  DeviceType = DeviceType;

  private protocolHandler: BulkCreationProtocolHandler;

  constructor(
    private csvImporter: CsvTileImporterService,
    private shadeTools: ShadeToolsService,
    private featureFlagsService: FeatureFlagsService,
    private formulaService: FormulaService
  ) {}

  ngOnInit(): void {
    // If protocol is provided (from device points view), use it
    if (this.protocol) {
      this.selectedProtocol = this.protocol;
      this.protocolHandler = BULK_CREATION_PROTOCOL_HANDLERS[this.protocol];
    } else {
      // If no protocol (from tiles/new view), select first available based on feature flags
      this.selectedProtocol = this.getDefaultProtocol();
      if (this.selectedProtocol) {
        this.protocolHandler = BULK_CREATION_PROTOCOL_HANDLERS[this.selectedProtocol];
      }
    }

    // Setup protocol menu options
    this.setupProtocolMenu();
  }

  private setupProtocolMenu(): void {
    const options: MotiOption<DeviceType>[] = [];

    if (this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_BACNET_BULK_CREATION)) {
      options.push({
        id: DeviceType.BACnet,
        name: DEVICE_TYPE_DISPLAY[DeviceType.BACnet],
      });
    }
    if (this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_MODBUS_BULK_CREATION)) {
      options.push({
        id: DeviceType.Modbus,
        name: DEVICE_TYPE_DISPLAY[DeviceType.Modbus],
      });
    }
    if (this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_LORAWAN_BULK_CREATION)) {
      options.push({
        id: DeviceType.LoRaWAN,
        name: DEVICE_TYPE_DISPLAY[DeviceType.LoRaWAN],
      });
    }

    if (this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_FORMULA_TILE_CREATION)) {
      options.push({
        id: DeviceType.Formula,
        name: DEVICE_TYPE_DISPLAY[DeviceType.Formula],
      });
    }

    this.protocolMenuGroup = new MotiOptionGroup<DeviceType>(options);
    this.toggleProtocolMenuGroup = new MotiOptionGroup<DeviceType>(
      options.filter(o => o.id !== DeviceType.Formula)
    );
  }

  private getDefaultProtocol(): DeviceType | null {
    const protocols = this.getAvailableProtocols();
    return protocols.length > 0 ? protocols[0] : null;
  }

  getAvailableProtocols(): DeviceType[] {
    const protocols: DeviceType[] = [];

    if (this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_BACNET_BULK_CREATION)) {
      protocols.push(DeviceType.BACnet);
    }
    if (this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_MODBUS_BULK_CREATION)) {
      protocols.push(DeviceType.Modbus);
    }
    if (this.featureFlagsService.isFeatureEnabled(FeatureFlag.ENABLE_LORAWAN_BULK_CREATION)) {
      protocols.push(DeviceType.LoRaWAN);
    }

    return protocols;
  }

  isProtocolSelectionEnabled(): boolean {
    // Only allow protocol selection when opened from tiles/new (no protocol provided)
    return !this.protocol && this.protocolMenuGroup.options.length > 1;
  }

  private hasPoints(): boolean {
    return !!(this.points && this.points.length > 0);
  }

  @HostListener('window:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    event.preventDefault();
    this.onClose();
  }

  onClose(): void {
    // Show confirmation if currently processing
    if (this.processingUpload) {
      this.shadeTools.startDialog(
        '<i class="fa-regular fa-circle-stop"></i> Stop Processing?',
        'CSV file is still being processed. Closing will stop the validation and return you to the previous screen.',
        [
          {
            text: 'Stop',
            color: DialogBoxButtonColor.Red,
            callback: () => {
              this.processingUpload = false;
              this.close.emit();
            }
          },
          {
            text: 'Cancel',
            color: DialogBoxButtonColor.Alt,
            callback: () => {
              // Just close the dialog
            }
          }
        ],
        false, false, true, true
      );
    } else {
      this.close.emit();
    }
  }

  // "analog" corresponds to "numeric" on frontend
  downloadNumericTilesTemplate(): void {
    if (this.isProtocolSelectionEnabled()) {
      this.numericMenuOpen = true;
      return;
    }

    const template = this.protocolHandler.generateNumericTemplate({
      deviceId: this.deviceId,
      points: this.points
    });

    const filename = this.protocolHandler.getNumericTemplateFilename(this.hasPoints());
    CSVTemplateGenerator.downloadCSV(template, filename).subscribe({
      error: (error) => {
        console.error('Failed to download template:', error);
        this.shadeTools.errorDialog(`Failed to download template: ${error.message || 'Unknown error'}`);
      }
    });
  }

  onNumericProtocolSelected(option: MotiOption<DeviceType>): void {
    this.numericMenuOpen = false;

    if (option.id === DeviceType.Formula) {
      this.formulaNumericMenuOpen = true;
      return;
    }

    const handler = BULK_CREATION_PROTOCOL_HANDLERS[option.id as keyof typeof BULK_CREATION_PROTOCOL_HANDLERS];
    const template = handler.generateNumericTemplate({
      deviceId: this.deviceId,
      points: this.points
    });

    const filename = handler.getNumericTemplateFilename(this.hasPoints());
    CSVTemplateGenerator.downloadCSV(template, filename).subscribe({
      error: (error) => {
        console.error('Failed to download template:', error);
        this.shadeTools.errorDialog(`Failed to download template: ${error.message || 'Unknown error'}`);
      }
    });
  }

  onNumericMenuClosed(): void {
    this.numericMenuOpen = false;
  }

  onFormulaTemplateSelected(option: MotiOption<string | null>): void {
    this.formulaNumericMenuOpen = false;
    CSVConfigExporter.downloadFormulaConfigTemplate(option.id).subscribe({
      error: (error) => {
        console.error('Failed to download formula template:', error);
        this.shadeTools.errorDialog(`Failed to download template: ${error.message || 'Unknown error'}`);
      }
    });
  }

  onFormulaNumericMenuClosed(): void {
    this.formulaNumericMenuOpen = false;
  }

  // "binary" corresponds to "toggle" on frontend
  downloadToggleTilesTemplate(): void {
    if (this.isProtocolSelectionEnabled()) {
      this.toggleMenuOpen = true;
      return;
    }

    const template = this.protocolHandler.generateToggleTemplate({
      deviceId: this.deviceId,
      points: this.points
    });

    const filename = this.protocolHandler.getToggleTemplateFilename(this.hasPoints());
    CSVTemplateGenerator.downloadCSV(template, filename).subscribe({
      error: (error) => {
        console.error('Failed to download template:', error);
        this.shadeTools.errorDialog(`Failed to download template: ${error.message || 'Unknown error'}`);
      }
    });
  }

  onToggleProtocolSelected(option: MotiOption<DeviceType>): void {
    this.toggleMenuOpen = false;

    const handler = BULK_CREATION_PROTOCOL_HANDLERS[option.id];
    const template = handler.generateToggleTemplate({
      deviceId: this.deviceId,
      points: this.points
    });

    const filename = handler.getToggleTemplateFilename(this.hasPoints());
    CSVTemplateGenerator.downloadCSV(template, filename).subscribe({
      error: (error) => {
        console.error('Failed to download template:', error);
        this.shadeTools.errorDialog(`Failed to download template: ${error.message || 'Unknown error'}`);
      }
    });
  }

  onToggleMenuClosed(): void {
    this.toggleMenuOpen = false;
  }

  tileGlossaryNewTab(): void {
    const protocol = this.selectedProtocol || this.getDefaultProtocol();
    if (!protocol) {
      this.shadeTools.errorDialog('No protocol available');
      return;
    }
    const anchor = protocol === DeviceType.Formula
      ? 'formula'
      : `numeric-${protocol.toLowerCase()}`;
    window.open(`/tile-glossary#${anchor}`, "_blank");
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Validate file using utility
      const validationError = CSVFileValidator.validateFile(file);
      if (validationError) {
        this.shadeTools.errorDialog(validationError);
        this.selectedFile = null;
        return;
      }

      this.selectedFile = file;
    }
  }

  formatFileSize(bytes: number): string {
    return CSVFileValidator.formatFileSize(bytes);
  }

  processCSVUpload(): void {
    if (!this.selectedFile) {
      this.shadeTools.errorDialog('Please select a file to upload');
      return;
    }

    this.processingUpload = true;

    const reader = new FileReader();
    reader.onload = (e) => {
      const csvContent = e.target?.result as string;

      Papa.parse(csvContent, {
        header: true,
        skipEmptyLines: true,
        transform: (value) => typeof value === 'string' ? value.trim() : value,
        complete: (results) => {
          if (results.errors.length > 0) {
            this.shadeTools.errorDialog(`CSV parsing error: ${results.errors[0].message}`);
            this.processingUpload = false;
            return;
          }

          // Map CSV rows to tiles upfront (single pass)
          const processingItems: TileProcessingItem[] = [];
          const allErrors: string[] = [];

          (results.data as any[]).forEach((row, index) => {
            const result = this.csvImporter.buildAndValidateTile(row, this.deviceId);

            const item: TileProcessingItem = {
              id: `tile-${Date.now()}-${index}`,
              csvData: row as any,
              tile: result.tile,
              formulaConfig: result.formulaConfig,
              status: result.errors.length > 0 ? TileProcessingStatus.ERROR_GENERAL : TileProcessingStatus.QUEUED,
              error: result.errors.join('; ')
            };

            processingItems.push(item);

            if (result.errors.length > 0) {
              allErrors.push(`Row ${index + 1}: ${result.errors.join(', ')}`);
            }
          });

          // Fail fast if any errors
          if (allErrors.length > 0) {
            const errorMessage = `Failed to process CSV:\n${allErrors.slice(0, 5).join('\n')}${allErrors.length > 5 ? `\n...and ${allErrors.length - 5} more errors` : ''}`;
            this.shadeTools.errorDialog(errorMessage);
            this.processingUpload = false;
            return;
          }

          // Validate custom formula expressions against the backend before proceeding
          const customFormulaItems = processingItems
            .map((item, i) => ({ item, rowNumber: i + 1 }))
            .filter(({ item }) => item.formulaConfig?.Points[0]?.Formula?.Type === FormulaType.Custom);

          if (customFormulaItems.length === 0) {
            this.proceedToProcessing.emit(processingItems);
            this.processingUpload = false;
            return;
          }

          this.validatingFormulas = true;

          forkJoin(
            customFormulaItems.map(({ item, rowNumber }) =>
              this.formulaService.validateExpression$(item.formulaConfig!.Points[0].Formula.Value).pipe(
                map(result => ({ rowNumber, result }))
              )
            )
          ).subscribe(validationResults => {
            this.validatingFormulas = false;

            const formulaErrors = validationResults
              .filter(({ result }) => !result.valid)
              .map(({ rowNumber, result }) => `Row ${rowNumber}: ${result.error ?? 'Invalid formula expression'}`);

            if (formulaErrors.length > 0) {
              const errorMessage = `Formula validation failed:\n${formulaErrors.slice(0, 5).join('\n')}${formulaErrors.length > 5 ? `\n...and ${formulaErrors.length - 5} more errors` : ''}`;
              this.shadeTools.errorDialog(errorMessage);
              this.processingUpload = false;
              return;
            }

            this.proceedToProcessing.emit(processingItems);
            this.processingUpload = false;
          });
        },
        error: (error) => {
          this.shadeTools.errorDialog(`Failed to parse CSV: ${error.message}`);
          this.processingUpload = false;
        }
      });
    };

    reader.onerror = () => {
      this.shadeTools.errorDialog('Failed to read file');
      this.processingUpload = false;
    };

    reader.readAsText(this.selectedFile);
  }


}
